import React, { useCallback, useMemo } from 'react'
import { type TourStep, type useTourValues } from 'utils/tour/tour.types'
import { useRPPTour } from 'utils/tour/tour.hooks'
import { useDomainMessages } from 'domains/pickup-settings/utils/domain/intl'
import { useNavContext } from 'gin-and-tonic/containers/nav/utils/NavContext'
import { usePickupTourStatus } from 'domains/pickup-settings/training-tour/usePickupTourStatus'
import { Status, useFeatureTourUpdateMutation } from 'domains/pickup-settings/api'
import { useRemindMeLaterStep } from './pickupRemindMeLaterTourStep'

export function usePickupOrderTour(): useTourValues {
  const { setSteps, isOpen } = useRPPTour()
  const { closeNav } = useNavContext()
  const [completeFeatureTour] = useFeatureTourUpdateMutation({
    variables: { name: 'pickup_orders', status: Status.Completed },
  })

  // detailed page tour cannot run for various reasons:
  // 1. the page is currently blocked by a welcome modal
  // 2. there is another tour going on on the same page
  // 3. this page tour is not enabled for this user
  // 3. this page is still loading or errors occurred
  const { isTourPending, refetch } = usePickupTourStatus('pickup_orders')
  const hasNoOtherTour = !isOpen
  const remindMeLaterStep = useRemindMeLaterStep()
  const messages = useDomainMessages({
    'step1.title': 'pickupSettingsDomain.tours.pickupOrder.step1.title',
    'step1.description.main': 'pickupSettingsDomain.tours.pickupOrder.step1.description.main',
    'step2.title': 'pickupSettingsDomain.tours.pickupOrder.step2.title',
    'step2.description.main1': 'pickupSettingsDomain.tours.pickupOrder.step2.description.main1',
    'step2.description.main2': 'pickupSettingsDomain.tours.pickupOrder.step2.description.main2',
    'step3.title': 'pickupSettingsDomain.tours.pickupOrder.step3.title',
    'step3.description.main': 'pickupSettingsDomain.tours.pickupOrder.step3.description.main',
  })

  const completeTour = useCallback(() => {
    completeFeatureTour()
    refetch()
  }, [completeFeatureTour, refetch])

  const steps: TourStep[] = useMemo(
    () => [
      {
        selector: '#orders.all',
        title: messages['step1.title'],
        position: ({ top, left }) => [left + 200, top + 100],
        onExitTour: () => {
          setSteps([remindMeLaterStep])
          completeTour()
        },
        description: messages['step1.description.main'],
        showPrevButton: false,
      },
      {
        selector: '#dashTable-head-order-id',
        title: messages['step2.title'],
        position: ({ top, left }) => [left + 50, top + 100],
        onExitTour: () => {
          setSteps([remindMeLaterStep])
          completeTour()
        },
        description: (
          <div>
            <p>{messages['step2.description.main1']}</p>
            <br />
            <p>{messages['step2.description.main2']}</p>
          </div>
        ),
      },
      {
        selector: '#dashTable-head-delivery-status',
        title: messages['step3.title'],
        beaconPosition: 'topRight',
        position: ({ top, left, width }) => [left - width, top + 100],
        description: messages['step3.description.main'],
        onEnterStep: completeTour,
      },
    ],
    [messages, remindMeLaterStep, setSteps, completeTour]
  )

  const startTour = useCallback(() => {
    if (isTourPending && hasNoOtherTour) {
      closeNav()
      setSteps(steps)
    }
  }, [closeNav, setSteps, steps, isTourPending, hasNoOtherTour])

  const redoTour = useCallback(() => setSteps(steps), [setSteps, steps])

  return { steps, startTour, redoTour }
}
